import {
  normalizeNavView,
  normalizeViewMode,
  setToStringArray,
  stringArrayToSet,
} from "./pageViewPreferenceState";

const EVENTS_VIEW_MODES = ["calendar", "table", "card"];
const EVENT_SIGNUPS_VIEW_MODES = ["table", "card"];

/** Events Management status filter includes Expired (beyond open/closed/all). */
export function normalizeEventListStatusFilter(status) {
  const v = status != null ? String(status).trim().toLowerCase() : "";
  return v === "open" || v === "closed" || v === "expired" || v === "all" ? v : null;
}

/**
 * Snapshot Event Management list toolbar filter/view state.
 * @param {object} source
 */
export function collectEventsListPagePreferenceState(source) {
  const {
    searchTerm,
    statusFilter,
    viewMode,
    navView,
    navDate,
    filterProgramId,
    filterOrganizerId,
  } = source || {};

  return {
    searchTerm: searchTerm != null ? String(searchTerm) : "",
    statusFilter: normalizeEventListStatusFilter(statusFilter) || "open",
    viewMode: normalizeViewMode(viewMode, EVENTS_VIEW_MODES) || "table",
    navView: normalizeNavView(navView) || "all",
    navDate:
      normalizeNavView(navView) === "all"
        ? ""
        : navDate != null
          ? String(navDate).trim()
          : "",
    filterProgramId: filterProgramId != null ? String(filterProgramId).trim() : "",
    filterOrganizerId: filterOrganizerId != null ? String(filterOrganizerId).trim() : "",
  };
}

/**
 * Apply saved Event Management preference state.
 * @param {object} state
 * @param {object} handlers
 */
export function applyEventsListPagePreferenceState(state, handlers) {
  if (!state || typeof state !== "object" || !handlers) return;

  const {
    setSearchTerm,
    setStatusFilter,
    setViewMode,
    setNavView,
    setNavDate,
    setFilterProgramId,
    setFilterOrganizerId,
  } = handlers;

  if (typeof setSearchTerm === "function") {
    setSearchTerm(state.searchTerm != null ? String(state.searchTerm) : "");
  }

  const status = normalizeEventListStatusFilter(state.statusFilter);
  if (status && typeof setStatusFilter === "function") {
    setStatusFilter(status);
  }

  const mode = normalizeViewMode(state.viewMode, EVENTS_VIEW_MODES);
  if (mode && typeof setViewMode === "function") {
    setViewMode(mode);
  }

  const navView = normalizeNavView(state.navView);
  if (navView && typeof setNavView === "function") {
    setNavView(navView);
  }
  if (typeof setNavDate === "function") {
    const dateTrim = state.navDate != null ? String(state.navDate).trim() : "";
    if (navView === "all") {
      setNavDate("");
    } else if (dateTrim) {
      setNavDate(dateTrim);
    }
  }

  if (typeof setFilterProgramId === "function") {
    setFilterProgramId(state.filterProgramId != null ? String(state.filterProgramId).trim() : "");
  }
  if (typeof setFilterOrganizerId === "function") {
    setFilterOrganizerId(
      state.filterOrganizerId != null ? String(state.filterOrganizerId).trim() : ""
    );
  }
}

/** Defaults after clearing a saved Events list view. */
export function getDefaultEventsListPreferenceResetPatch() {
  return {
    searchTerm: "",
    statusFilter: "open",
    viewMode: "table",
    navView: "all",
    navDate: "",
    filterProgramId: "",
    filterOrganizerId: "",
  };
}

/**
 * Snapshot Event Sign-ups toolbar filter/view state.
 * @param {object} source
 */
export function collectEventSignupsPagePreferenceState(source) {
  const {
    searchTerm,
    statusFilter,
    viewMode,
    navView,
    navDate,
    filterProgramId,
    attendanceFilter,
  } = source || {};

  return {
    searchTerm: searchTerm != null ? String(searchTerm) : "",
    statusFilter: setToStringArray(statusFilter),
    viewMode: normalizeViewMode(viewMode, EVENT_SIGNUPS_VIEW_MODES) || "table",
    navView: normalizeNavView(navView) || "all",
    navDate:
      normalizeNavView(navView) === "all"
        ? ""
        : navDate != null
          ? String(navDate).trim()
          : "",
    filterProgramId: filterProgramId != null ? String(filterProgramId).trim() : "",
    attendanceFilter: attendanceFilter != null ? String(attendanceFilter).trim() : "",
  };
}

/**
 * Apply saved Event Sign-ups preference state.
 * @param {object} state
 * @param {object} handlers
 */
export function applyEventSignupsPagePreferenceState(state, handlers) {
  if (!state || typeof state !== "object" || !handlers) return;

  const {
    setSearchTerm,
    setStatusFilter,
    setViewMode,
    setNavView,
    setNavDate,
    setFilterProgramId,
    setAttendanceFilter,
  } = handlers;

  if (typeof setSearchTerm === "function") {
    setSearchTerm(state.searchTerm != null ? String(state.searchTerm) : "");
  }

  if (typeof setStatusFilter === "function") {
    // Empty array = All statuses (matches drill-in / All UI).
    setStatusFilter(stringArrayToSet(state.statusFilter));
  }

  const mode = normalizeViewMode(state.viewMode, EVENT_SIGNUPS_VIEW_MODES);
  if (mode && typeof setViewMode === "function") {
    setViewMode(mode);
  }

  const navView = normalizeNavView(state.navView);
  if (navView && typeof setNavView === "function") {
    setNavView(navView);
  }
  if (typeof setNavDate === "function") {
    const dateTrim = state.navDate != null ? String(state.navDate).trim() : "";
    if (navView === "all") {
      setNavDate("");
    } else if (dateTrim) {
      setNavDate(dateTrim);
    }
  }

  if (typeof setFilterProgramId === "function") {
    setFilterProgramId(state.filterProgramId != null ? String(state.filterProgramId).trim() : "");
  }
  if (typeof setAttendanceFilter === "function") {
    setAttendanceFilter(state.attendanceFilter != null ? String(state.attendanceFilter).trim() : "");
  }
}

/** Defaults after clearing a saved Event Sign-ups view. */
export function getDefaultEventSignupsPreferenceResetPatch() {
  return {
    searchTerm: "",
    statusFilter: new Set(["Open"]),
    viewMode: "table",
    navView: "all",
    navDate: "",
    filterProgramId: "",
    attendanceFilter: "",
  };
}
